import moment from "moment-timezone";
import _ from "lodash";

import { OrderOrigin } from "src/core/orders/api/orders.api";
import { OrderTotalByOriginResult } from "src/core/dashboard/api/widget-data.api";

import { OrderTotalByOriginGraphValue } from "../OrderTotalsByOriginWidget";
import {
  DisplayRangeType,
  groupByDisplayRange
} from "../../utils";
import { GraphDataType } from "../../WidgetTotalValueSwitcher";

/**
 * Helper to group order totals based on xAxisBy preference
 *
 * @param orderTotalsByOrigin A list of flat order totals by origin objects
 * @param displayRange DisplayRangeType
 * @param xAxisBy  "date" | "origin"
 * @returns
 *
 * ### Example.
 *
 * **In:**
 * ```
 * const orderTotalsByOrigin = [
 *  {
      "origin": "manual",
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1,
      "value": 8.509999871253967
    },
    {
      "origin": "manual",
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1,
      "value": 8.509999871253967
    },
    {
      "origin": "manual",
      "date": "Thu Feb 03 2022 00:00:00",
      "count": 1,
      "value": 8.509999871253967
    }
 * ]
 * const displayRange = "month"
 * const xAxisBy = "date"
 * ```
 * **Out:**
 * ```
 *[
    {
      "date": "2022-01-01T00:00:00.000Z",
      "manual": 17.019999742507935
    },
    {
      "date": "2022-02-01T00:00:00.000Z",
      "manual": 8.509999871253967
    }
  ]
 * ```
 */
export const groupOrderTotalsByOrigin = (orderTotalsByOrigin: OrderTotalByOriginResult, xAxisBy: "date" | "origin", displayRange?: DisplayRangeType, graphDataType?: GraphDataType): OrderTotalByOriginGraphValue[] => {
  const result: OrderTotalByOriginGraphValue[] = [];
  // Group the data based on xAxisBy value
  const groupedOriginsData = xAxisBy === "date" && displayRange ? groupByDisplayRange(orderTotalsByOrigin, displayRange) : _.groupBy(orderTotalsByOrigin, "origin");

  // Loop grouped data to form the result, ensuring to include xAxisBy property and all relevant values accumulated
  for (const xAxisGroup in groupedOriginsData) {
    if (xAxisBy === "date") {
      // accumulative totals for xAxisBy date
      const accumulativeDateTotals = {
        [ OrderOrigin.API ]: 0,
        [ OrderOrigin.DuplicateOrder ]: 0,
        [ OrderOrigin.File ]: 0,
        [ OrderOrigin.List ]: 0,
        [ OrderOrigin.Manual ]: 0,
        [ OrderOrigin.Prediction ]: 0,
        [ OrderOrigin.Reminder ]: 0,
        [ OrderOrigin.StandingOrder ]: 0
      };

      groupedOriginsData[ xAxisGroup ].forEach(row => {
        accumulativeDateTotals[ row.origin ] += graphDataType === "total" ? row.count : row.value;
      });

      const dataItem: OrderTotalByOriginGraphValue = {
        date: moment.utc(xAxisGroup).toDate(),
        // Only include origins that have values
        ..._.pickBy(accumulativeDateTotals, value => value !== 0)
      };

      // Force header to include date day if displayRange not by month
      if (displayRange !== "month") {
        dataItem.toolTipHeader = moment.utc(xAxisGroup).format("Do MMMM YYYY");
      }

      result.push(dataItem);
    } else {
      // accumulative total for xAxisBy origin
      let accumulativeTotal = 0;

      groupedOriginsData[ xAxisGroup ].forEach(row => {
        accumulativeTotal += row.value;
      });

      const dataItem = {
        origin: xAxisGroup as OrderOrigin,
        value: accumulativeTotal,
        toolTipHeader: _.startCase(xAxisGroup.replace(/[_-]/g, " "))
      };

      result.push(dataItem);
    }
  }

  return result;
};
